import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import CommonModal from '../../../shared/components/modal/common-modal'
import { Col, Flex, Form, Radio, Row, Space, Typography } from 'antd'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { ErrorMessage } from '@hookform/error-message'
import { useTranslation } from 'react-i18next'
import CustomDatePicker from '../../../shared/components/common/date-picker'
import '../index.scss'
import { SYSTEM_MESSAGE_IDS } from '../../../shared/constants/systemMessages.constants'
import { FORMAT_DATE } from '../../../shared/constants/app.constants'
import { RangePickerProps } from 'antd/es/date-picker'
import dayjs from 'dayjs'
import { useSelector } from 'react-redux'
import { getDefaultTimeZone } from 'src/app/store/masterSetup'
import { useDatePicker } from 'src/app/shared/hooks/useDatePicker'

export enum State {
  publishNow = 1,
  schedulePublishing,
}

export interface ISchedulePublishing {
  publishing: State
  schedulePublishing: any
}

interface PropsSchedulePublishingModal {
  confirmLoading?: boolean
  onConfirm?: (data: ISchedulePublishing) => any
  defaultValueProps?: ISchedulePublishing
  ref?: any
}

const SchedulePublishingModal: React.FC<PropsSchedulePublishingModal> = forwardRef(
  ({ onConfirm, confirmLoading, defaultValueProps }, ref) => {
    const { t } = useTranslation()
    const [isShowModal, setIsShowModal] = useState<boolean>(false)
    const { getDatePickerDate } = useDatePicker()
    const defaultTimeZone = useSelector(getDefaultTimeZone)

    const defaultValues: ISchedulePublishing = {
      publishing: State.publishNow,
      schedulePublishing: null,
    }

    const {
      control,
      formState: { errors, touchedFields, isValid },
      handleSubmit,
      setValue,
      clearErrors,
      setError,
      reset,
      getValues,
      trigger,
      unregister,
    } = useForm<any>({
      mode: 'all',
      defaultValues,
      // resolver: yupResolver(generateValidationSchedulePublishingSchema(t)),
    })

    const publishing = useWatch({
      control,
      name: 'publishing',
    })

    const schedulePublishing = useWatch({
      control,
      name: 'schedulePublishing',
    })

    const handleOpenModal = () => {
      setIsShowModal(true)
    }

    const handleCloseModal = () => {
      setIsShowModal(false)
      if (!defaultValueProps) {
        reset(defaultValues)
      }
      clearErrors()
    }

    const onSubmit = (data: ISchedulePublishing) => {
      // @ts-ignore

      onConfirm({
        ...data,
        schedulePublishing:
          data?.schedulePublishing && publishing === State.schedulePublishing
            ? getDatePickerDate(data.schedulePublishing).toISOString()
            : undefined,
      })
    }

    const isSubmitButtonDisabled = publishing === State.schedulePublishing && !schedulePublishing

    const validateForm = async () => {
      if (publishing === State.publishNow) {
        await unregister('schedulePublishing')
        setValue('schedulePublishing', null, { shouldValidate: true, shouldTouch: true })
        clearErrors()
      }
    }

    const disabledDate: RangePickerProps['disabledDate'] = (current) => {
      return current && current < dayjs().tz(defaultTimeZone?.code).endOf('day')
    }

    const setDataDefaultForm = (data: ISchedulePublishing) => {
      reset(data)
    }

    const handleClickSubmit = async () => {
      if (publishing === State.publishNow) {
        await unregister('schedulePublishing')
      }
      handleSubmit(onSubmit)()
    }

    useImperativeHandle(
      ref,
      () => {
        return {
          openModal() {
            handleOpenModal()
          },
          closeModal() {
            handleCloseModal()
          },
          setDataDefaultForm(data: ISchedulePublishing) {
            setDataDefaultForm(data)
          },
        }
      },
      [],
    )

    useEffect(() => {
      validateForm()
    }, [publishing])

    useEffect(() => {
      if (defaultValueProps) {
        reset(defaultValueProps)
      }
    }, [defaultValueProps])

    return (
      <CommonModal
        title={t('HelpPage:form.label.schedulePublishing')}
        open={isShowModal}
        okText={t('HelpPage:form.button.confirm')}
        okButtonProps={{
          disabled: isSubmitButtonDisabled,
          style: { width: 102, fontWeight: 600 },
          size: 'large',
        }}
        cancelText={t('HelpPage:form.button.cancel')}
        cancelButtonProps={{ style: { width: 102, fontWeight: 600 }, size: 'large' }}
        onOk={handleClickSubmit}
        onCancel={handleCloseModal}
        confirmLoading={confirmLoading}
        onClose={handleCloseModal}
      >
        <Form
          name='schedule-publishing-form'
          className='form-container schedule-publishing-form'
          layout='vertical'
          style={{ padding: 0 }}
          onFinish={handleSubmit(onSubmit)}
        >
          <Flex vertical>
            <Row gutter={24}>
              <Col span={24}>
                <Form.Item label={t('HelpPage:form.label.publishing')} className='isRequired'>
                  <Row>
                    <Col span={7}>
                      <Controller
                        name='publishing'
                        control={control}
                        render={({ field, fieldState }) => (
                          <Radio.Group
                            {...field}
                            name={'publishing'}
                            style={{ marginTop: 16 }}
                            onChange={(event) => {
                              setValue('publishing', event?.target?.value)
                            }}
                          >
                            <Space direction='vertical' size={24}>
                              <Radio value={State.publishNow}>
                                <Typography.Text style={{ fontSize: 16, fontWeight: 400 }}>
                                  {t('HelpPage:form.label.publishNow')}
                                </Typography.Text>
                              </Radio>

                              <Radio value={State.schedulePublishing}>
                                <Space size={16}>
                                  <Typography.Text
                                    style={{
                                      fontSize: 16,
                                      fontWeight: 400,
                                    }}
                                  >
                                    {t('HelpPage:form.label.schedulePublishing')}
                                  </Typography.Text>
                                </Space>
                              </Radio>
                            </Space>
                          </Radio.Group>
                        )}
                      />
                    </Col>

                    <Col span={17}>
                      <Flex vertical justify='flex-start' style={{ transform: 'translateY(50px)' }}>
                        <Controller
                          name='schedulePublishing'
                          control={control}
                          rules={{
                            required: {
                              value: getValues('schedulePublishing') === null,
                              message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.FIELD_IS_REQUIRED, {
                                Required_Field: `${t('HelpPage:form.label.schedulePublishing')}`,
                              }),
                            },
                          }}
                          render={({ field }) => (
                            <>
                              <CustomDatePicker
                                {...field}
                                onBlur={async () => {
                                  field.onBlur()
                                  await trigger('schedulePublishing')
                                }}
                                format={FORMAT_DATE}
                                disabled={publishing === State.publishNow ? true : false}
                                style={{ width: 455 }}
                                disabledDate={disabledDate}
                                placeholder={t('HelpPage:form.label.selectDate')}
                                inputReadOnly
                                showToday={false}
                                allowClear={false}
                                defaultPickerValue={dayjs().tz(defaultTimeZone?.code)}
                              />

                              {publishing === State.publishNow && (
                                <div
                                  style={{
                                    height: 30,
                                    width: 30,
                                    position: 'absolute',
                                    right: 15,
                                    top: 10,
                                    cursor: 'no-drop',
                                  }}
                                ></div>
                              )}
                            </>
                          )}
                        />
                      </Flex>
                    </Col>
                  </Row>
                </Form.Item>

                <Typography className='error-message' style={{ paddingLeft: 190, marginTop: '-10px' }}>
                  <ErrorMessage errors={errors} name='schedulePublishing' />
                </Typography>
              </Col>
            </Row>
          </Flex>
        </Form>
      </CommonModal>
    )
  },
)

export default SchedulePublishingModal
